import React, { useContext, useEffect } from 'react'
import {
	FormBuilder,
	formKeySelector,
	updateControlState,
} from 'formBuilder/src'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import CircleSpinner from 'components/circle-spinner/CircleSpinner'
import {
	ApplicationDispatchContext,
	LOAN_APPLICATION_ACTIONS,
} from 'contexts/ApplicationContext'
import { IApplicationInformation } from 'interfaces/i-application-information'
import { getLanguageLocale, getMarket } from 'utils/countryHelpers'
import { IApplicationContext } from 'interfaces/i-application-context-props'
import { FormFactory } from 'formBuilder/formFactory'
import { CustomerForm } from 'constants/enums/formTypes'

import './UpdateBankInfo.scss'

const FORM_NAME = 'bankInfoFormIt'

function validateItalianIbanChecksum(iban: string): boolean {
	const upper = iban.toUpperCase()
	const rearranged = upper.slice(4) + upper.slice(0, 4)
	const numeric = rearranged.replace(/[A-Z]/g, (c) =>
		(c.charCodeAt(0) - 55).toString(),
	)
	let remainder = 0
	for (const char of numeric) {
		remainder = (remainder * 10 + parseInt(char, 10)) % 97
	}
	return remainder === 1
}

interface IProps {
	// The multistep header above the page (see msvItaly.tsx) already covers this content when there's no calculator shown alongside this form, so this header would otherwise duplicate it.
	includeHeader?: boolean
}

export const UpdateBankInfoIt = ({ includeHeader = true }: IProps) => {
	const { t } = useTranslation()
	const { loanApplication, dispatchLoanApplication }: IApplicationContext =
		useContext(ApplicationDispatchContext)
	const dispatch = useDispatch()

	const formSchema = FormFactory(
		CustomerForm.ApplicationBankInfoForm,
		{
			formName: FORM_NAME,
			blockName: FORM_NAME,
			market: getMarket(),
			locale: getLanguageLocale(),
			getDataUrl: '',
			validationErrorList: false,
			requiredMessage: t('ValidateRequired'),
		},
		loanApplication.applicationData as IApplicationInformation,
	)

	const formStateValues = useSelector(
		formKeySelector(FORM_NAME, 'controlsState'),
	)

	useEffect(
		() => {
			dispatchLoanApplication({
				type: LOAN_APPLICATION_ACTIONS.SET_UPDATE_APPLICATIONDATA,
				payload: {
					...loanApplication,
					updateApplicationData: {
						...loanApplication.updateApplicationData,
						bankInfo: {
							...loanApplication.updateApplicationData?.bankInfo,
							iban: formStateValues?.ibanVal.value
								?.replaceAll(' ', '')
								.toUpperCase(),
						},
					},
				},
			})
		},
		[formStateValues?.ibanVal.value], // eslint-disable-line react-hooks/exhaustive-deps
	)

	const ibanValue = formStateValues?.ibanVal?.value as string | undefined

	useEffect(() => {
		if (!ibanValue) return
		if (!/^(i|I)(t|T)([0-9]{2}[A-Za-z][0-9]{22})$/.test(ibanValue)) return
		if (!validateItalianIbanChecksum(ibanValue)) {
			dispatch(
				updateControlState({
					formName: FORM_NAME,
					controlName: 'ibanVal',
					isValid: false,
					validationMessage: t('InvalidIban'),
				}),
			)
		}
	}, [ibanValue]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<div className="update-bank-info">
			{includeHeader && (
				<div className="update-bank-info-header">
					<h3>{t('UpdateBankInfoHeader')}</h3>
				</div>
			)}
			{formSchema ? (
				<FormBuilder
					formSchema={formSchema}
					apiError={undefined}
					showErrorsInForm={true}
					showSubmitButton={false}
				/>
			) : (
				<CircleSpinner />
			)}
		</div>
	)
}
